// React-hook facade around the singleton SSE event-bus. Components use
// this to subscribe to specific event-types or to read the live
// connection status. The actual connection is owned by a single bus
// per tab — see lib/sseBus.ts.
//
// Activation: call `useEventBusConnection()` once near the app root
// (App.tsx) to drive the connection lifecycle. All other components use
// `useEventBus()` for subscriptions; they don't need to know whether
// the connection is up or not — handlers fire when events arrive,
// silence means nothing's happening (or the bus is reconnecting).

import { useEffect, useState } from 'react';
import { getSseBus, type BusEvent, type BusEventType, type BusStatus } from '../lib/sseBus';

/**
 * Mount the SSE connection. Call this exactly once at the top of the
 * component tree (App.tsx). On unmount the bus disconnects.
 */
export function useEventBusConnection() {
  useEffect(() => {
    const bus = getSseBus();
    bus.connect();
    return () => bus.disconnect();
  }, []);
}

/**
 * Subscribe to a single event-type for the lifetime of the component.
 * Handler must be stable (useCallback) or wrapped in a ref by the
 * caller — we re-subscribe whenever it changes, which is fine for
 * occasional UI updates but wasteful for hot paths.
 */
export function useBusSubscribe<T extends BusEventType>(
  type: T,
  handler: (ev: Extract<BusEvent, { type: T }>) => void,
) {
  useEffect(() => {
    const bus = getSseBus();
    return bus.subscribe(type, handler);
  }, [type, handler]);
}

/**
 * Live status of the SSE connection. Used by the footer status-dot so
 * the user can see whether realtime updates are flowing or polling-
 * fallback is doing the work. Returns 'idle' before connect() has been
 * called or after disconnect().
 */
export function useBusStatus(): BusStatus {
  const [status, setStatus] = useState<BusStatus>(() => getSseBus().status());
  useEffect(() => {
    const bus = getSseBus();
    return bus.onStatus(setStatus);
  }, []);
  return status;
}
